import { Injectable } from '@angular/core';
import { isBefore } from 'date-fns'
import { NavigateService, AuthService, UtilsService, LanguageService, SigntrustService, PermissionsService, ModulesService, UserPinAuthService } from '@services/index.service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  ErrorCode,
  Response,
  LoginByCertificateRequest,
  LocalStorageKeys,
  AngularPaths,
  UserAuth,
  LoginByTokenRequest,
  UserSession,
  Module,
  Permission,
} from '@models/index.model';
import { Router } from '@angular/router';

@Injectable()
export class UserPinSessionService {
  private loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private _navigate: NavigateService,
    private router: Router,
    private _userPinAuth: UserPinAuthService,
    private _utils: UtilsService,
    private _lang: LanguageService,
    private _signtrustServ: SigntrustService,
    private _permiss: PermissionsService,
    private _modules: ModulesService
  ) { }

  public initLocalUserSession(loginForm: LoginRequest): Observable<any> {
    return this.buildUserSession(this._userPinAuth.logIn(loginForm)).pipe(
      tap(_ => {
        this.routerRedirectBeforeLogin();
      }),
      catchError(err => {
        if (err.status === 405 && err.error.errorCode === ErrorCode.CHANGE_PASSWORD) {
          this.cleanPersonalLocalStorage();
          this._setLoggedIn(false);
          // TODO: implement new case for userPin
          // this._navigate.setUrl(`/${AngularPaths.PASSWORD_UPDATE}`, { username: loginForm.username, update: true });
        } else {
          this.removeBrowserSession();
        }
        return throwError(() => err);
      })
    );
  }

  public initCertificateSession(loginByCertificateRequest: LoginByCertificateRequest): Observable<any> {
    return this.buildUserSession(this._userPinAuth.loginByCertificate(loginByCertificateRequest)).pipe(
      tap(_ => {
        this.routerRedirectBeforeLogin();
      }),
      catchError(err => {
        this.removeBrowserSession();
        return throwError(() => err);
      })
    );
  }

  public initTokenSession(loginByTokenRequest: LoginByTokenRequest): Observable<any> {
    return this.buildUserSession(this._userPinAuth.loginByToken(loginByTokenRequest)).pipe(
      tap(_ => {
        this.routerRedirectBeforeLogin();
      }),
      catchError(err => {
        this.removeBrowserSession();
        return throwError(() => err);
      })
    );
  }

  public getUserAuth(): UserAuth {
    return (this._utils.getLocalStorage(LocalStorageKeys.USER_PIN_SESSION) as UserSession)?.userAuth;
  }

  public routerRedirectBeforeLogin() {
    const userPinRedirectId = localStorage.getItem('userPinRedirectId');
    if (userPinRedirectId) {
      this.router.navigate([`/${AngularPaths.CONFIG_USER_PIN }/${userPinRedirectId}`], { replaceUrl: true });
    } else {
      this.removeBrowserSession();
    }
  }

  public removeBrowserSession() {
    this.cleanPersonalLocalStorage();
    this._setLoggedIn(false);
  }

  public getAccessToken(): string {
    const session: UserAuth = this.getUserAuth();
    return session?.accessToken;
  }

  public isLoggedIn(): boolean {
    const session: UserAuth = this.getUserAuth();
    let isLogged: boolean = false;
    if (session) {
      isLogged = isBefore(new Date(), new Date(session.expiration));
    }
    return isLogged;
  }

  public loggedIn$(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  private _setLoggedIn(loggedIn: boolean) {
    this.loggedIn.next(loggedIn);
  }

  public buildUserSession(authRequest: Observable<Response<LoginResponse>>): Observable<UserSession> {
    return authRequest.pipe(
      map((res: Response<LoginResponse>) => this._utils.mapResponseData<LoginResponse>(res)),
      map((loginResponse: LoginResponse) => ({
        userAuth: {
          accessToken: loginResponse.accessToken,
          refreshToken: loginResponse.refreshToken,
          expiration: loginResponse.expiration,
        },
        userData: {
          friendlyName: loginResponse.sessionConfig.friendlyName,
          email: loginResponse.sessionConfig.email,
          language: this._lang.normalizeLang(loginResponse.sessionConfig.language),
          domainName: loginResponse.sessionConfig.domainName,
          domainType: loginResponse.sessionConfig.domainType,
        },
        userPermissions: loginResponse.sessionConfig.permissions,
        userModules: loginResponse.sessionConfig.modules,
      })),
      tap((res: UserSession) => {
        this._utils.setLocalStorage(LocalStorageKeys.USER_PIN_SESSION, res);
        this._lang.configureApplicationLanguage();
        this._setLoggedIn(true);
      })
    );
  }

  public checkTokenValid(): boolean {
    const session: UserAuth = this.getUserAuth();
    return !!session && !!session.accessToken;
  }

  private cleanPersonalLocalStorage() {
    localStorage.removeItem(LocalStorageKeys.USER_PIN_SESSION);
    localStorage.removeItem(LocalStorageKeys.USER_PIN_REDIRECT_ID);
  }
}
